import React from "react";
import { Grid, Divider, withStyles } from "@material-ui/core";

import { v4 as uuidv4 } from "uuid";
import constants from "./constants";
import {
  Action,
  AddTrailer,
  DeleteTrailer,
  DispatchInput,
  Detail,
  Header,
  Notes,
  DispatchError,
  DefectDetails,
} from "./components";
import { getTractorError, getDollyError, getTrailerError, isStraightTruck, getUpdatedTrailerError } from "./utils";
import { EQUIPMENT_DEFECTS_GRID_FILTER } from "./EquipmentDefects/constants";
import { isEqual } from "lodash/lang";

const styles = (theme) => ({
  container: {
    flexGrow: 1,
    overflowY: "auto",
  },
  detailContainer: {
    padding: "0px 20px",
  },
  divider: {
    backgroundColor: theme.gridIconBorderColor,
    opacity: 0.6,
  },
});

class DispatchTrailer extends React.Component {
  constructor(props) {
    super(props);
    this.generateTrailerList();
    this.state = {
      trailerDetails: [this.getNewTrailer()],
      trailerDetailsErrors: [null],
      tractor: null,
      notes: "",
      tractorError: null,
      filteredTrailerList: this.props.trailerList,
      filteredRouteList: this.props.routeList,
    };
  }

  generateTrailerList = () => {
    this.trailerListWithoutStraightTruck = this.props.trailerList.filter(
      (trailer) =>  !isStraightTruck(trailer.trailerType?.toUpperCase())
    );
  }
  
  componentDidUpdate(prevProps){
    if(prevProps.allTrailers !== this.props.allTrailers){
      this.generateTrailerList();
      this.updateState();
    }
  }

  updateState = () => {
    this.setState((state) => ({
      trailerDetails: state.trailerDetails.map(eachTrailer => {
        const trailer = this.props.allTrailers.find(
          (equipment) => equipment.trailerId === eachTrailer?.trailerId
        ) ?? this.getNewTrailer();
        const dolly = this.props.dollyList.find(eachDolly => eachDolly.value === eachTrailer.dolly?.value) ?? null;
        return {
          ...trailer,
          dolly
        }
      }),
      tractor: this.props.tractorList.find(tractor =>
            tractor.value === state.tractor?.value
          ) ?? null
    }), this.refreshErrors)
  }

  refreshErrors = ()=>{
    this.refreshTrailerError()
     if (this.state.tractor) {
       this.setState((state)=>({
         tractorError: getTractorError(state.tractor),
       }));
     }
    
  }

  getNewTrailer = () => {
    return {
      id: uuidv4(),
      trailerNumber: null,
      manifestNumber: null,
      routeName: null,
      dolly: null,
    };
  };

  updateNotes = (notes) => {
    this.setState({
      notes,
    });
  };

  replaceTrailer = (updateIndex, data) => {
    const trailerData = data || this.getNewTrailer();
    // If selected trailer type is straight truck, remove other trailers since we cannot attach trailers to straight truck
    if (isStraightTruck(trailerData?.trailerType?.toUpperCase())) {
      const straightTractor = this.props.tractorList.find(
        (tractor) => tractor.label === trailerData.trailerNumber
      ) ?? null;
      this.onChangeTractor(straightTractor);
    } else {
      this.setState(
        (state) => ({
          trailerDetails: state.trailerDetails.map((trailer, index) => {
            if (index === updateIndex) {
              return {
                ...trailerData,
              };
            }
            return trailer;
          }),
          tractor:
            state.tractor?.isStraightTruck ? null : state.tractor, //clear tractor if trailer is cleared or changed and selected tractor was a straight tractor
        }),
        () => this.updateTrailerError(updateIndex)
      );
    }
  };

  getValidTrailersByRoute = (routeName) => {
    const trailerStatusInOrder = constants.TRAILER_STATUS_ALLOWED;
    return this.props.allTrailers.filter(
        (trailer) =>
          trailerStatusInOrder.includes(trailer.trailerStatus) &&
          trailer.routeName?.slice(-3) === routeName
      ) ?? [];    
  };

  updateTrailerAndRoute = (updateIndex, field) => (value) => {
    let trailerData = null;
    let preSelectRouteTrailers = null;
    if (field === "Trailer") {
      trailerData = this.props.allTrailers.find(
        (equipment) => equipment.trailerId === value?.value
      );
    } else if (field === "RouteName") {
      const routeName = value?.value ?? null;
      preSelectRouteTrailers = this.getValidTrailersByRoute(routeName);
      const selectRouteTrailerData = this.getValidTrailersByRoute(
        this.state.trailerDetails[updateIndex]?.routeName?.slice(-3)
      ).map(trailer=>trailer.trailerId);
      trailerData = routeName === null && selectRouteTrailerData.length > 0 &&
        selectRouteTrailerData.includes(this.state.trailerDetails[updateIndex]?.trailerId) 
          ? null
          : {
              ...this.state.trailerDetails[updateIndex],
              ...(preSelectRouteTrailers?.length > 0 && {
                ...preSelectRouteTrailers[0],
              }),
              routeName,
            };
    }
    //dont replace trailer if same trailer selected again
    if (!isEqual(trailerData, this.state.trailerDetails[updateIndex])) {
      this.setState(
        {
          filteredTrailerList: this.getFilteredTrailerList(
            this.props.trailerList,
            trailerData?.routeName,
            updateIndex
          ),
          filteredRouteList: this.getFilteredRouteList(
            this.props.routeList,
            trailerData
          ),
        },
        () => {
          this.replaceTrailer(updateIndex, trailerData);
        }
      );
    }
  };

  updateDolly = (updateIndex) => (dolly) => {
    this.setState(
      (state) => ({
        trailerDetails: state.trailerDetails.map((trailer, index) => {
          if (index === updateIndex) {
            return {
              ...trailer,
              dolly,
            };
          }
          return trailer;
        }),
      }),
      () => this.updateDollyError(updateIndex)
    );
  };

  updateDollyError = (updateIndex) => {
    this.setState((state) => ({
      trailerDetailsErrors: state.trailerDetailsErrors.map((error, index) => {
        if (index === updateIndex) {
          const dollyError = getDollyError(
            state.trailerDetails[index].dolly,
            state.trailerDetails
          );
          // update newly updated dolly error
          return {
            ...error,
            dolly: dollyError,
          };
        }
        // if any trailer had duplicate dolly error, re-evaulate error with new data
        if (error?.dolly === constants.ERROR_MESSAGES.DUPLICATE_DOLLY) {
          return {
            ...error,
            dolly: getDollyError(
              state.trailerDetails[index].dolly,
              state.trailerDetails
            ),
          };
        }
        return error;
      }),
    }));
  };

  updateTrailerError = (updateIndex) => {
    this.setState((state) => ({
      trailerDetailsErrors: isStraightTruck(
        state.trailerDetails[0]?.trailerType
      )
        ? [
            {
              trailerNumber: getTrailerError(
                state.trailerDetails[0],
                state.trailerDetails
              ),
            },
          ]
        : state.trailerDetailsErrors.map((error, index) => {
            if (index === updateIndex) {
              const trailerError = getTrailerError(
                state.trailerDetails[index],
                state.trailerDetails
              );
              // update newly updated trailer error
              return {
                trailerNumber: trailerError,
              };
            }
            // if any trailer had duplicate trailer error, re-evaulate error with new data
            if (
              error?.trailerNumber ===
              constants.ERROR_MESSAGES.DUPLICATE_TRAILER
            ) {
              return {
                trailerNumber: getTrailerError(
                  state.trailerDetails[index],
                  state.trailerDetails
                ),
              };
            }
            return error;
          }),
    }));
  };

  onChangeTractor = (tractor) => {
    const index=0;
    const tractorDetails = this.props.tractorList.find(
      (equipment) => equipment.label === tractor?.label
    );

    if (tractorDetails?.isStraightTruck === true &&
      this.state.tractor?.label !== tractorDetails?.label
    ) {
      const selectedTrailer = this.props.allTrailers.find(
        (equipment) => equipment.trailerNumber === tractorDetails?.label
      );
      const isStraightTrailer = isStraightTruck(
        selectedTrailer?.trailerType?.toUpperCase()
      );
      const trailerDetails = isStraightTrailer ? selectedTrailer : this.getNewTrailer();
      // check if trailer is a straight trailer
      // if straight than update the trailer with matching tractor otherwise clear the dropdown of trailer
      if (this.state.trailerDetails[index]?.trailerNumber !== trailerDetails?.trailerNumber) {
         this.setState(
      {
        filteredTrailerList: this.getFilteredTrailerList(
          this.props.trailerList,
          trailerDetails.routeName,
          index
        ),
        filteredRouteList: this.getFilteredRouteList(
          this.props.routeList,
          trailerDetails
        ),
      },
      () =>
        this.updateTrailerDetails([trailerDetails], tractorDetails)
    )
      }else{
        this.updateTractor(tractorDetails);
      }
    } else if (!tractorDetails || tractorDetails?.isStraightTruck === false) {
      // check if new tractor selected is not straight but previously selected tractor was straight truck , then we need to clear the previous selected straight trailer also
      if (this.state.tractor?.isStraightTruck === true) {
        const trailerDetails=this.getNewTrailer();
         this.setState(
      {
        filteredTrailerList: this.getFilteredTrailerList(
          this.props.trailerList,
          trailerDetails.routeName,
          index
        ),
        filteredRouteList: this.getFilteredRouteList(
          this.props.routeList,
          trailerDetails
        ),
      },
      () =>
        this.updateTrailerDetails([trailerDetails], tractorDetails)
    )
      }else{
        this.updateTractor(tractorDetails);
      }
    }
  };


  updateTrailerDetails = (trailerDetails, tractor) => {
    this.setState(
      {
        trailerDetails,
      },
      () => {
        this.updateTrailerError(0);
        this.updateTractor(tractor);
      }
    );
  };

  updateTractor = (tractor) => {
      this.setState({
        tractor,
        tractorError: getTractorError(tractor),
      });
  };

  addNewTrailer = () => {
    const newTrailerData = this.getNewTrailer();
    this.setState((state) => ({
      trailerDetails: [...state.trailerDetails, newTrailerData],
      trailerDetailsErrors: [...state.trailerDetailsErrors, null],
    }));
  };

  refreshTrailerError = () => {
    this.setState({
      trailerDetailsErrors: getUpdatedTrailerError(
        this.state.trailerDetails,
        this.state.trailerDetailsErrors
      )
    })
  }

  removeTrailer = (removeIndex) => {
    this.setState((state) => ({
      trailerDetails: state.trailerDetails.filter(
        (_trailer, index) => index !== removeIndex
      ),
      trailerDetailsErrors: state.trailerDetailsErrors.filter(
        (_error, index) => index !== removeIndex
      ),
    }), () => this.refreshTrailerError()
    );
  };

  getTrailerDetailsError = (trailer, index) => {
    const trailerError = getTrailerError(trailer, this.state.trailerDetails);
    const dollyError = index
      ? getDollyError(trailer.dolly, this.state.trailerDetails)
      : null;
    if (trailerError || dollyError) {
      return {
        trailerNumber: trailerError,
        dolly: dollyError,
      };
    }
    return null;
  };

  isValidTrailerDispatch = () => {
    let trailerDetailsErrors = [];
    this.state.trailerDetails.forEach((trailerDetail, index) =>
      trailerDetailsErrors.push(
        this.getTrailerDetailsError(trailerDetail, index)
      )
    );
    const tractorError = getTractorError(this.state.tractor);
    this.setState({
      trailerDetailsErrors,
      tractorError,
    });
    return !Boolean(
      tractorError || trailerDetailsErrors.filter(Boolean).length
    );
  };

  checkIfStraightTruck = (trailerDetails) => {
    return (
      trailerDetails.length === 1 &&
      isStraightTruck(trailerDetails[0].trailerType?.toUpperCase())
    );
  }

  dispatchTrailer = () => {
    const data = this.isValidTrailerDispatch()
      ? {
          tractorNumber: this.state.tractor?.label,
          notes: this.state.notes,
          trailerDetails: this.state.trailerDetails.map((trailer) => ({
            trailerNumber: trailer.trailerNumber,
            manifestNumber: trailer.manifestNumber || null,
            dollyNumber: trailer.dolly?.label || null,
            routeName: trailer.routeName || null,
          })),
        }
      : null;
    this.props.dispatchDriver(data);
  };

  renderDivider = (index) =>
    index <= this.state.trailerDetails.length - 1 ? (
      <Divider className={this.props.classes.divider} />
    ) : null;

  getFilteredTrailerList = (trailerListData, routeName, index) => {
    const trailerList =
      index > 0 ? this.trailerListWithoutStraightTruck : trailerListData;
    const list =
      routeName &&
      trailerList.filter((trailer) => {
        return trailer?.routeName?.slice(-3) === routeName.slice(-3);
      });
    return list?.length > 0 ? list : trailerList;
  };

  getFilteredRouteList = (routeList, data) => {
    let list = [];
    if (data?.routeName || data?.trailerId) {
      const trailerList = this.props.trailerList
        .filter((trailer) => {
          return (
            trailer.routeName === data.routeName ||
            trailer.value === data.trailerId
          );
        })
        .map((trailer) => trailer.routeName?.slice(-3) || null);
      list = routeList.filter((route) => {
        return trailerList.includes(route.value);
      });
    }
    return list?.length > 0 ? list : routeList;
  };
  

  render() {
    const {
      trailerDetails,
      trailerDetailsErrors,
      filteredTrailerList,
      filteredRouteList,
    } = this.state;
    const { dollyList, tractorList, isPreselectedDriver } = this.props;

    const isStraightTruckSelected = this.checkIfStraightTruck(trailerDetails) || this.state.tractor?.isStraightTruck

    const addTrailerCount = isStraightTruckSelected
      ? 0
      : constants.MAX_ADD_TRAILER_DETAILS_ALLOWED - trailerDetails.length;
    const addTrailers = new Array(addTrailerCount).fill("addTrailers");

    const { classes } = this.props;
    return (
      <>
        <Grid className={`${classes.container} customScroll`}>
          <Header label={"TRAILER DETAILS"} />
          {trailerDetails.map((trailer, index) => (
            <React.Fragment key={trailer.id}>
              {/* Shows delete option for from 2nd trailer */}
              {index ? (
                <DeleteTrailer id={index} onClick={this.removeTrailer} />
              ) : null}
              <DispatchInput
                label="Trailer #"
                autoFocus={isPreselectedDriver}
                value={trailer.trailerNumber ?? null}
                options={filteredTrailerList}
                onChange={this.updateTrailerAndRoute(index, "Trailer")}
                error={trailerDetailsErrors?.[index]?.trailerNumber}
                isClearable={true}
              />
              {Boolean(trailer?.defects?.length) && (
                <DefectDetails
                  defects={trailer?.defects}
                  equipmentType={"trailers"}
                  openDvirReports={this.props.openDvirReports(
                    EQUIPMENT_DEFECTS_GRID_FILTER.trailer
                  )}
                />
              )}
              <DispatchInput
                label="Manifest #"
                value={trailer.manifestNumber ?? null}
                disableEdit
                isClearable={true}
              />
              <DispatchInput
                label="Route Name"
                autoFocus={false}
                value={trailer.routeName?.slice(-3) || null} //compare last 3 char of trailer's routeName and routeList's routeName
                options={filteredRouteList}
                onChange={this.updateTrailerAndRoute(index, "RouteName")}
                isClearable={true}
              />
              {/* Dolly needed only for 2nd or more trailers are attached */}
              {index > 0 ? (
                <>
                  <DispatchInput
                    label="Dolly #"
                    autoFocus={false}
                    value={trailer.dolly?.label ?? null}
                    options={dollyList}
                    onChange={this.updateDolly(index)}
                    error={trailerDetailsErrors?.[index]?.dolly}
                    isClearable={true}
                  />
                  {Boolean(trailer.dolly?.defects?.length) && (
                    <DefectDetails
                      defects={trailer.dolly?.defects}
                      equipmentType={"dolly"}
                      openDvirReports={this.props.openDvirReports(
                        EQUIPMENT_DEFECTS_GRID_FILTER.dolly
                      )}
                    />
                  )}
                </>
              ) : null}
              <Grid container className={classes.detailContainer}>
                <Detail
                  label={"Trailer Type"}
                  value={trailer.trailerName ?? "__"}
                />
                <Detail
                  label={"Trailer Status"}
                  value={trailer.trailerStatus ?? "__"}
                />
                <Detail
                  label={"Ideal Start Time"}
                  value={trailer.idealStartTime ?? "__:__"}
                />
              </Grid>
              {this.renderDivider(index)}
            </React.Fragment>
          ))}

          {addTrailers.map((_newTrailer, index) => (
            <AddTrailer key={index} onClick={this.addNewTrailer} />
          ))}
          <Header label={"TRACTOR DETAILS"} />
          <DispatchInput
            label="Tractor #"
            autoFocus={false}
            value={this.state.tractor?.label ?? null}
            options={tractorList}
            onChange={this.onChangeTractor}
            isClearable={true}
            error={this.state.tractorError}
          />
          {Boolean(this.state.tractor?.defects?.length) && (
            <DefectDetails
              defects={this.state.tractor?.defects}
              equipmentType={"tractors"}
              openDvirReports={this.props.openDvirReports(
                EQUIPMENT_DEFECTS_GRID_FILTER.tractor
              )}
            />
          )}
          <Notes value={this.state.notes} onChange={this.updateNotes} />
          {this.props.dispatchError ? (
            <DispatchError message={this.props.dispatchError} />
          ) : null}

          {this.props.children}
        </Grid>
        <Action
          cancel={this.props.cancel}
          confirm={this.dispatchTrailer}
          disableButton={this.props.disableDispatch}
        />
      </>
    );
  }
}

export default withStyles(styles)(DispatchTrailer);
